import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.js";

interface Props {
  open: boolean;
  currentDeadline: Date | string | null;
  onConfirm: (deadline: Date | null) => void;
  onCancel: () => void;
}

function toDateInputValue(d: Date | string | null): string {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  // Format as YYYY-MM-DD in local time
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function DeadlineEditDialog({ open, currentDeadline, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const [deadlineStr, setDeadlineStr] = useState(() => toDateInputValue(currentDeadline));

  const handleConfirm = () => {
    const deadline = deadlineStr ? new Date(deadlineStr) : null;
    onConfirm(deadline);
  };

  const handleClear = () => {
    onConfirm(null);
  };

  return (
    <ConfirmDialog
      open={open}
      title={t("club.description.deadlineEditTitle")}
      confirmLabel={t("common.save")}
      cancelLabel={t("common.cancel")}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className="space-y-2">
        <label className="text-xs font-medium" style={{ color: "var(--color-muted-foreground)" }}>
          {t("club.description.deadlineLabelEdit")} <span style={{ opacity: 0.6 }}>{t("club.description.deadlineClearHint")}</span>
        </label>
        <input
          type="date"
          className="w-full rounded-md px-3 py-1.5 text-sm transition-all focus:outline-none"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-background)",
            color: "var(--color-foreground)",
          }}
          value={deadlineStr}
          onChange={(e) => setDeadlineStr(e.target.value)}
          onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          autoFocus
        />
        {currentDeadline && (
          <button
            type="button"
            className="text-xs transition-colors"
            style={{ color: "var(--color-muted-foreground)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-destructive)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
            onClick={handleClear}
          >
            {t("club.description.deadlineClearButton")}
          </button>
        )}
      </div>
    </ConfirmDialog>
  );
}
